import type { PostHog } from 'posthog-js';
import * as Enums from '@/utils/enums';

// The token is only baked into builds from main (see build-deploy.yml), and events are only sent from the
// live app, so UAT, staging, dev slots and localhost never reach PostHog even if a token is present.
const PROJECT_TOKEN = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN as string | undefined;
const API_HOST = 'https://eu.i.posthog.com';
const LIVE_HOSTNAME = 'app.servcraft.co.za';

export const isPostHogEnabled = () =>
    !!PROJECT_TOKEN && typeof window !== 'undefined' && window.location.hostname === LIVE_HOSTNAME;

let client: Promise<PostHog> | null = null;

// Imported on use: storage pulls in helper, which would make this service an import cycle for its callers.
const loadStorage = async () => (await import('@/utils/storage')).default;

// Loaded on first use so the SDK isn't in the main bundle and nothing runs until there is an event to send.
// Everything automatic is off: we only send events we capture explicitly.
const getClient = () => {
    if (!client) {
        client = Promise.all([import('posthog-js'), loadStorage()]).then(([{ default: posthog }, Storage]) => {
            const userID = Storage.getCookie(Enums.Cookie.userID);
            posthog.init(PROJECT_TOKEN!, {
                api_host: API_HOST,
                autocapture: false,
                capture_pageview: false,
                capture_pageleave: false,
                capture_exceptions: false,
                capture_heatmaps: false,
                capture_dead_clicks: false,
                capture_performance: false,
                rageclick: false,
                disable_session_recording: true,
                disable_surveys: true,
                disable_web_experiments: true,
                disable_product_tours: true,
                disable_conversations: true,
                disable_external_dependency_loading: true,
                advanced_disable_flags: true,
                person_profiles: 'identified_only',
                request_batching: false,
                // Attribute events to the signed-in user without sending a separate $identify event.
                ...(userID ? { bootstrap: { distinctID: String(userID), isIdentifiedID: true } } : {}),
            });
            return posthog;
        });
    }
    return client;
};

export type CaptureProperties = Record<string, unknown>;

/** Our superadmin signs in as "Admin" (see ProfileMenu); its sessions are internal, so they're never sent. */
export const isInternalUser = (email: unknown) => String(email ?? '').trim().toLowerCase() === 'admin';

/**
 * Sends one event, attributed to the signed-in user and tagged with their company. `setOnce` lands
 * on the person and is never overwritten (e.g. the first time something happened).
 */
const capture = async (event: string, properties: CaptureProperties = {}, setOnce?: CaptureProperties) => {
    if (!isPostHogEnabled()) return;
    try {
        const Storage = await loadStorage();
        const email = Storage.getCookie(Enums.Cookie.servUserName);
        if (isInternalUser(email)) return;
        const posthog = await getClient();
        const name = Storage.getCookie(Enums.Cookie.servFullName);
        const company = Storage.getCookie(Enums.Cookie.servCompanyName);
        posthog.capture(event, {
            ...properties,
            tenant_id: Storage.getCookie(Enums.Cookie.tenantID) || null,
            company_name: company ? String(company) : null,
            $set: {
                ...(email ? { email: String(email) } : {}),
                ...(name ? { name: String(name) } : {}),
            },
            ...(setOnce ? { $set_once: setOnce } : {}),
        });
    } catch {
        // Analytics must never break the app.
    }
};

const posthogService = {
    capture,
};

export default posthogService;
